/**
 * Clear Offline Data
 *
 * Wipes all local offline data: SQLite OPFS, Service Worker cache,
 * and unregisters the Service Worker.
 * Used on logout to protect patient data - and, by clearLeftoversOfEndedSession, when a session ended without it.
 * The other tabs of the browser are told (followCleaningOfOtherTabs): one file is shared by all of them.
 */

'use client';

import { forgetSyncState } from './sync-state';
import { stopBackgroundSync } from './sync';

const DB_FILE = 'clirxos-cache.sqlite3';
/** Tables of the offline database that mirror the server. The write queue is not among them: it is the user's unsaved work. */
export const READ_TABLES = ['drugs', 'patients', 'queue', 'settings', 'services', 'prescriptions'];

/** Pause between two attempts to delete a file that is held. */
const RETRY_MS = 150;
/** Attempts a log-out makes (about 5 s, a little under the cap it waits for), and the login page's cleaning of what an ended session left (about 15 s: nobody waits for it). */
const LOGOUT_ATTEMPTS = 36;
const LEFTOVER_ATTEMPTS = 100;

/** Delete the offline database's files from the browser's private file system. Retried: the file is held while a transaction runs - in this tab or in another - or a worker goes away. */
async function removeOpfsFiles(attempts: number): Promise<void> {
  try {
    if (!(navigator.storage && navigator.storage.getDirectory)) return;
    const root = await navigator.storage.getDirectory();
    const held: string[] = [];
    for (const name of [DB_FILE, `${DB_FILE}-journal`, `${DB_FILE}-wal`]) {
      let gone = false;
      for (let attempt = 0; attempt < attempts && !gone; attempt++) {
        try { await root.removeEntry(name, { recursive: false }); gone = true; }
        catch (error) {
          if ((error as { name?: string })?.name === 'NotFoundError') { gone = true; break; } // not there: as good as deleted
          await new Promise(resolve => setTimeout(resolve, RETRY_MS)); // locked by a transaction that is ending, or by a worker that is going away
        }
      }
      if (!gone) held.push(name);
    }
    if (held.length) console.warn('[OfflineData] OPFS files still held, not deleted:', held.join(', '));
    else console.log('[OfflineData] OPFS SQLite files deleted');
  } catch (error) {
    console.error('[OfflineData] OPFS delete failed:', error);
  }
}

/** The legacy IndexedDB cache (src/lib/cache.ts). Only asked: another tab's open connection would keep a delete waiting. */
async function deleteLegacyIndexedDb(): Promise<void> {
  try {
    if (window.indexedDB) {
      window.indexedDB.deleteDatabase('pms-cache');
      console.log('[OfflineData] Requested IndexedDB delete');
    }
  } catch { /* nothing to do */ }
}

async function deleteCacheStorage(): Promise<void> {
  try {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    console.log(`[OfflineData] Cleared ${cacheNames.length} SW caches`);
  } catch (error) {
    console.error('[OfflineData] Cache clear failed:', error);
  }
}

async function unregisterServiceWorkers(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
    console.log(`[OfflineData] Unregistered ${registrations.length} Service Workers`);
  } catch (error) {
    console.error('[OfflineData] SW unregister failed:', error);
  }
}

const CHANNEL = 'clirxos-offline-clean';
let channel: BroadcastChannel | null | undefined;
/** The channel the tabs of this browser tell each other on. One per page; a channel does not hear its own messages. */
function cleaningChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    try { channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL); } catch { channel = null; }
  }
  return channel;
}

/**
 * The cleaning begins. This page forgets what the sync remembers (when it last ran, which version of each list it holds) and stops
 * syncing; the other tabs of this browser are told to do the same and to let go of the database file. First, and before anything is
 * awaited: a sync that believes it is up to date leaves the next sign-in with an empty cache, and a sync still running in another tab
 * (one file is shared by all of them, and a transaction of that tab keeps this one from deleting it) stamps the emptied cache "current"
 * the moment it ends.
 */
let cleaningBegun = false;
function beginCleaning(): void {
  forgetSyncState();
  if (cleaningBegun) return; // once per page: the stamps are forgotten again, the sync stopped and the other tabs told only the first time
  cleaningBegun = true;
  stopBackgroundSync();
  try { cleaningChannel()?.postMessage('clean'); } catch { /* the other tabs find out at their next 401 */ }
}

/**
 * Called by every tab that uses the offline data: when another tab begins deleting them, do what that tab's own page does - forget the
 * sync's state, stop syncing, close the database (which waits behind a transaction of this tab still running, and then lets go of the
 * file) - without telling the others again. Returns the function that stops listening.
 */
export function followCleaningOfOtherTabs(): () => void {
  const listening = cleaningChannel();
  if (!listening) return () => undefined;
  const heard = () => {
    forgetSyncState();
    stopBackgroundSync();
    void import('./db').then(db => db.closeOfflineDB()).catch(() => undefined);
  };
  listening.addEventListener('message', heard);
  return () => listening.removeEventListener('message', heard);
}

/**
 * Clear all offline data — call on logout
 */
export async function clearOfflineData(opts: { skipLegacyIndexedDb?: boolean; opfsAttempts?: number } = {}): Promise<void> {
  console.log('[OfflineData] Clearing all offline data...');

  // 0. The sync's state is forgotten, the sync stopped and the other tabs told - first, before anything is awaited.
  beginCleaning();

  // 1. The offline database is closed before its files are deleted, and the other cleaners do not wait for that: closing waits behind a
  //    transaction still running (a sync writing a list, which holds the file - a delete then fails), and nothing a sync step still to come
  //    wants to write is accepted any more.
  const closeThenDelete = async () => {
    try { await (await import('./db')).closeOfflineDB(); } catch { /* the database was never opened */ }
    await removeOpfsFiles(opts.opfsAttempts ?? LOGOUT_ATTEMPTS);
  };
  const steps = [closeThenDelete(), deleteCacheStorage(), unregisterServiceWorkers()];
  if (!opts.skipLegacyIndexedDb) steps.push(deleteLegacyIndexedDb());
  await Promise.allSettled(steps);

  console.log('[OfflineData] All offline data cleared');
}

export interface LogoutCleaning {
  /** everything finished before the cap */
  done: boolean;
  ms: number;
}

/**
 * What a log-out cleans: everything at once, and the log-out WAITS for it - up to `capMs`, so that a cleaning that hangs cannot keep the
 * user from signing out. (It used to wait one second and sign out whatever was left: a sync writing the offline database, or another tab
 * holding the legacy cache stores, left the whole database of patients in the browser.) `legacyCleaners` empty the legacy IndexedDB
 * cache (src/lib/cache.ts); that database is deleted only AFTER they are done - asked for together, a delete waiting for the open
 * connection and transactions waiting behind the delete hold each other until the cap - and the rest does not wait for them.
 * `onSlow` is called once if the cleaning is still running after `slowAfterMs`, so that the screen can say what it is waiting for.
 * The sync's stamps are forgotten synchronously, before anything is awaited.
 */
export async function clearForLogout(
  opts: { legacyCleaners?: Array<() => Promise<unknown>>; capMs?: number; slowAfterMs?: number; onSlow?: () => void } = {}
): Promise<LogoutCleaning> {
  const { legacyCleaners = [], capMs = 6000, slowAfterMs = 1000, onSlow } = opts;
  const started = Date.now();
  forgetSyncState();
  const legacy = async () => {
    await Promise.allSettled(legacyCleaners.map(clean => Promise.resolve().then(clean)));
    await deleteLegacyIndexedDb();
  };
  const work = Promise.allSettled([clearOfflineData({ skipLegacyIndexedDb: true }), legacy()]);
  let done = false;
  work.then(() => { done = true; });
  const slow = setTimeout(() => { if (!done) onSlow?.(); }, slowAfterMs);
  let capTimer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([work, new Promise<void>(resolve => { capTimer = setTimeout(resolve, capMs); })]);
  clearTimeout(slow);
  clearTimeout(capTimer);
  return { done, ms: Date.now() - started };
}

export type Leftovers =
  | 'unknown'        // the server could not be asked, or did not answer in a way that says: nothing was touched
  | 'session-alive'  // the server says there is a session: nothing was touched
  | 'cleared';       // the server says there is no session: everything this browser kept was deleted, but the writes that were never sent

/** A write that was never sent is the user's unsaved work. One that was sent ('done') is a copy of data the server has. */
const UNSENT = `status IN ('pending', 'failed', 'syncing')`;

/**
 * What the server says about the session, asked now. 'none' only for a 200 whose body is an empty JSON: `null`, which is how the session
 * endpoint says "nobody", or `{}`. Everything else is 'unknown': no network, no answer within `ms`, a 401/403/5xx, a page that is not
 * JSON (a captive portal, a proxy), JSON that is broken or is not a session.
 */
async function sessionAccordingToServer(ms: number): Promise<'none' | 'alive' | 'unknown'> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const res = await fetch('/api/auth/session', { cache: 'no-store', credentials: 'same-origin', signal: controller.signal });
    if (res.status !== 200) return 'unknown';
    const body: unknown = await res.json();
    if (body === null) return 'none';
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      if ((body as { user?: unknown }).user) return 'alive';
      if (Object.keys(body).length === 0) return 'none';
    }
    return 'unknown';
  } catch {
    return 'unknown';
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The offline database, if it can be opened here and now. On the login page the SQLite engine comes through the service worker's cache or
 * not at all: it is served from /wasm/, which the route guard does not serve to a browser without a session (it answers the login page).
 */
async function openIfPossible(ms: number) {
  try {
    const { getOfflineDB } = await import('./db');
    return await Promise.race([getOfflineDB(), new Promise<null>(resolve => setTimeout(() => resolve(null), ms))]);
  } catch {
    return null;
  }
}
type OfflineDb = NonNullable<Awaited<ReturnType<typeof openIfPossible>>>;

async function countUnsentWrites(db: OfflineDb): Promise<number> {
  const rows = await db.query(`SELECT COUNT(*) AS n FROM write_queue WHERE ${UNSENT}`);
  return Number(rows[0]?.n ?? 0);
}

/**
 * Empty every table but the write queue, drop the writes that were already sent from it, and give the space back: VACUUM rewrites the file,
 * so no page of what was deleted stays in it. Every table that is there is emptied, not a list of known ones - a table added later holds
 * patient data too.
 */
async function emptyAllButUnsentWrites(db: OfflineDb): Promise<void> {
  const tables: Array<{ name: string }> = await db.query(
    `SELECT name FROM sqlite_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%' AND name NOT IN ('write_queue', '_meta')`
  );
  await db.executeTransaction([
    ...tables.map(t => ({ sql: `DELETE FROM "${t.name}"` })),
    { sql: `DELETE FROM write_queue WHERE NOT (${UNSENT})` },
  ]);
  await db.execute('VACUUM');
}

/**
 * A session can end without the log-out having cleaned: the sign-out was cut short, the cookie expired, the browser was closed in the
 * middle. What the browser kept - the patients' list, the queue, the prescriptions - is then there for whoever opens the app next on that
 * machine. Called where every browser without a session ends up (the login page), it deletes it - but ONLY on the server's word:
 * /api/auth/session asked now, online, answering with an empty JSON. Offline, no answer, a status other than 200, a page that is not JSON,
 * JSON of another shape, a live session: nothing is touched (a clinic that lost its network, or whose proxy is acting up, must keep working
 * offline).
 *
 * Writes that were never sent are the user's work, and an explicit log-out is what throws them away. So here they are looked at first: when
 * the database can be opened and holds some, everything else is emptied and they stay (the count is logged, never the contents) for the
 * next sign-in to send. A database that cannot be opened here - the SQLite engine is not served without a session, and the service worker
 * may not have it - or cannot be read is deleted with the rest: what is in it cannot be said, and leaving patients' data on a shared PC
 * is the worse of the two.
 */
export async function clearLeftoversOfEndedSession(
  opts: { askMs?: number; openMs?: number } = {}
): Promise<{ outcome: Leftovers; keptWrites: number }> {
  const nothing = (outcome: Leftovers) => ({ outcome, keptWrites: 0 });
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return nothing('unknown');
  const session = await sessionAccordingToServer(opts.askMs ?? 8000);
  if (session === 'alive') return nothing('session-alive');
  if (session !== 'none') return nothing('unknown');

  beginCleaning(); // before the database is opened: the other tabs let go of the file
  const db = await openIfPossible(opts.openMs ?? 5000);
  if (db) {
    try {
      const unsent = await countUnsentWrites(db);
      if (unsent > 0) {
        await emptyAllButUnsentWrites(db);
        try { await (await import('./db')).closeOfflineDB(); } catch { /* already closed */ }
        await Promise.allSettled([deleteCacheStorage(), unregisterServiceWorkers(), deleteLegacyIndexedDb()]);
        console.log(`[OfflineData] Kept ${unsent} unsent writes (contents not read); everything else deleted`);
        return { outcome: 'cleared', keptWrites: unsent };
      }
    } catch (error) {
      console.error('[OfflineData] Could not look at the write queue, deleting everything:', error);
    }
  }
  await clearOfflineData({ opfsAttempts: LEFTOVER_ATTEMPTS });
  return nothing('cleared');
}

/**
 * Auto-purge stale cached data (older than maxAgeMs)
 * Default: 24 hours without sync = purge
 */
export async function autoPurgeStaleData(maxAgeMs = 24 * 60 * 60 * 1000): Promise<void> {
  try {
    const { getOfflineDB } = await import('./db');
    const db = await getOfflineDB();
    if (!db) return;

    const cutoff = Date.now() - maxAgeMs;

    // Check last sync time
    const syncMeta = await db.query(
      `SELECT table_name, last_synced_at FROM sync_meta ORDER BY last_synced_at DESC LIMIT 1`
    );

    if (syncMeta.length > 0 && syncMeta[0].last_synced_at) {
      const lastSync = Number(syncMeta[0].last_synced_at);
      if (lastSync < cutoff) {
        console.log(`[OfflineData] Last sync was ${Math.round((Date.now() - lastSync) / 3600000)}h ago — purging stale cache`);

        // Purge read caches (keep write queue — those are user's unsaved work!)
        for (const table of READ_TABLES) {
          try {
            await db.execute(`DELETE FROM ${table}`);
          } catch {}
        }
        await db.execute(`DELETE FROM sync_meta`);

        console.log('[OfflineData] Stale read cache purged (write queue preserved)');
      }
    }
  } catch (error) {
    console.error('[OfflineData] Auto-purge failed:', error);
  }
}
